// src/components/Racks/rackHelpers.js
//
// S3-02.3 — display helpers ported out of the deleted RacksGrid.js.
// Behaviour is unchanged except that the hard-coded 'Tadpole 1B' debug
// probes went away with the old file.
import { formatDateOnly } from '../../utils/dateUtils';
import {
  ageTitle, clutchStageWord, firstDateLabel, isLayNotSeen, isStageNotSet,
  LAY_NOT_SEEN, stageWordFor,
} from '../../utils/ageFormat';
// ⚠️ THE NAME OF THE CLUTCHES PAGE'S OPTION IS THAT PAGE'S CONSTANT
// (Cowork run 22, finding 10). This file spelled it with an em dash and
// `clutchRowHelpers.EMPTY_SHOWN_OPTION` — the string the `<select>`
// actually renders — spelled it with a hyphen, so the sentence sending an
// operator to that control named it in words that are not on it. One
// constant; the module is helpers only (no React, no CSS, no cycle).
import { EMPTY_SHOWN_OPTION } from '../Facilities/clutchRowHelpers';
// Prod hotfix 2026-10-05 — the ONE animal identity (handle, sex word,
// species/morph), shared with the lot picker.
import { animalHandle, animalKindText } from '../Facilities/animalRowHelpers';
import { sexDisplay } from '../../constants/enums';
import { formatSpeciesName } from '../../utils/scientificName';

export const RACK_KIND_SUGGESTIONS = [
  'display rack',
  'wire rack',
  'tub rack',
  'cart',
  'incubator',
  'furniture',
];

/** Tags whose tanks are husbandry-irrelevant and hidden by default. */
export const MUTED_TAGS = ['feeder', 'feeder_culture', 'feeder culture', 'storage'];

export const isMutedTank = (tank) =>
  (tank?.tags || []).some((t) => MUTED_TAGS.includes(String(t?.name || t).toLowerCase()));

export const formatDateMMDDYYYY = (dateStr) => {
  if (!dateStr) return null;
  return formatDateOnly(dateStr, { year: 'numeric', month: '2-digit', day: '2-digit' });
};

/**
 * The drawer's sex chip — "♂ Male" / "♀ Female" / "Unsexed", from the ONE
 * `sexDisplay`. It used to be a bare M / F / "?" in a dark badge whose
 * text the racks page's own `.rk-page span { color: inherit }` rescue
 * painted the card's near-black: a solid black square on prod (hotfix
 * 2026-10-05). The colour half of that fix is in racks-v2.css.
 */
export const getGenderDisplay = (gender, basis) => sexDisplay(gender, basis).label;

export const getStageLabel = (stage) => {
  const stageMap = {
    terrestrial: 'Froglet',
    tadpole: 'Tadpole',
    eggs: 'Eggs',
    // S3-01.1: gecko taxon vocabulary.
    hatched: 'Hatched',
    egg: 'Egg',
  };
  return stageMap[stage] || stage;
};

/**
 * The age "Who's inside" prints — THE SERVER'S `age_display` ("20m", "2y",
 * "9w", "5d"), never a ladder of our own (Cowork run 33 review, S3). This
 * used to re-derive the number from `age_days`/`age_weeks`/… by STAGE, so a
 * tadpole kept two years read "891d" here beside "2y" on the Animals grid
 * — the very split D5 closed. `facility/taxon_rules.age_display` is the one
 * ladder; `utils/ageFormat` only ever spells it out.
 */
export const formatAge = (animal) => {
  const shown = animal && animal.age_display;
  return shown == null || shown === '' ? null : String(shown);
};

export const getReferenceDateLabel = (animal) => {
  if (animal?.out_of_water_date) return 'OOW';
  if (animal?.hatch_date) return 'Hatch';
  // The first date's word is the tooltip's (Cowork run 36, H5): "Lay" for
  // an egg, "Birth" otherwise — `utils/ageFormat.firstDateLabel`, ONE rule
  // for the line and the tooltip on both drawers.
  if (animal?.birth_date) return firstDateLabel(animal);
  return null;
};

/**
 * THE TANK FACE'S OCCUPANT COUNT — one sentence for the desktop card and
 * the phone row.
 *
 * Cowork run 23, finding 11: a tank holding only a clutch read "0 animals
 * · 1 clutch". "0 animals" there is a number about nothing the keeper is
 * looking at; the face says "1 clutch". A tank with animals, or with
 * neither, reads as it always did ("2 animals · 1 clutch", "0 animals").
 */
export const occupantFaceText = (animalCount, clutchCount) => {
  const animals = Number(animalCount) || 0;
  const clutches = Number(clutchCount) || 0;
  const clutchPart = clutches
    ? `${clutches} clutch${clutches === 1 ? '' : 'es'}` : '';
  if (!animals && clutches) return clutchPart;
  const animalPart = `${animals} animal${animals === 1 ? '' : 's'}`;
  return clutchPart ? `${animalPart} · ${clutchPart}` : animalPart;
};

/** "Who's inside"'s age tooltip — the ONE rule, with the drawer's dates.
 *  The stage word is the badge's own (`stage_label`, Cowork run 45 review),
 *  so the tooltip never says "terrestrial" beside a "Morphling" badge; an
 *  older API without it keeps the coarse word. */
const drawerAgeTitle = (record, kind) => ageTitle(record, {
  kind,
  formatDate: (d) => formatDateMMDDYYYY(d) || 'N/A',
  // A clutch says "Egg" where the Animals grid does (Cowork run 52, C).
  stageLabel: kind === 'clutch' ? clutchStageWord(record) : stageWordFor(record),
});

/** Two-line label for one animal row in the occupant drawer. */
export const formatAnimalDisplay = (animal) => {
  const age = formatAge(animal);
  const refDateLabel = getReferenceDateLabel(animal);
  const refDate = animal.out_of_water_date || animal.hatch_date || animal.birth_date;
  const dateDisplay = refDateLabel && refDate
    ? `${refDateLabel}: ${formatDateMMDDYYYY(refDate)}`
    : null;

  // S3-05.3: 'On Hold' LEFT this line and became a chip
  // (`presenceChip`), because saying it twice on one row is exactly the
  // clutter the 2026-09-26 ruling is about. 'Deceased' stays only as a
  // belt: the racks page no longer draws a deceased animal at all (the
  // server's `present=true` drops it), so this branch is unreachable from
  // the board and would only ever fire for a caller that asked for
  // everything.
  const flags = [
    animal.is_for_sale ? 'For Sale' : null,
    animal.is_deceased ? 'Deceased' : null,
  ].filter(Boolean).join(' • ');

  const line2Parts = [age ? `Age: ${age}` : null, dateDisplay, flags].filter(Boolean);
  // ⚠️ THE ROW LEADS WITH THE ANIMAL'S NAME (Cowork run 23, finding 12).
  // After a split, "Who's inside" read five identical "Tadpole ·
  // Agalychnis callidryas" rows — five animals with names, and not one of
  // them said. A NAMED animal leads with its name and keeps its species /
  // morph after it; an unnamed one keeps today's species line exactly.
  //
  // Prod hotfix 2026-10-05: an UNNAMED animal leads with its short id
  // ("#F7B7C3") — the same handle the Animals grid, the lot picker and the
  // storefront print — because "Dendrobates tinctorius — Azureus" twice
  // over says nothing about WHICH animal is which. The species half is the
  // ONE `animalKindText` (common name, locality, morph; the scientific
  // name when the species carries no common name).
  return {
    line1: [animalHandle(animal), animalKindText(animal)].filter(Boolean).join(' — '),
    line2: line2Parts.length ? line2Parts.join(' • ') : null,
    // The age spelled out with its stage, then its dates — the grids' own
    // tooltip (`utils/ageFormat.ageTitle`, Cowork run 34 E5).
    ageTitle: age ? drawerAgeTitle(animal, 'animal') : null,
    stage: animal.stage,
    // Cowork run 45 — the WORD the badge prints. The server's
    // `stage_label` (`taxon_rules.stage_label`) gives a frog out of the
    // water its feeding band ("Morphling", "Adult"), the label Feeding
    // History prints on that frog's rows; an older API without it keeps
    // the coarse word ("Froglet").
    stageLabel: stageWordFor(animal) || (animal.stage ? getStageLabel(animal.stage) : null),
    // Cowork run 46 — an animal no date places ("Stage not set") draws a
    // MUTED badge with a tooltip saying why, never the "Eggs" a dateless
    // row falls to on the coarse ladder.
    stageNotSet: isStageNotSet(stageWordFor(animal)),
    gender: animal.gender,
    sexBasis: animal.sex_basis,
    sex: sexDisplay(animal.gender, animal.sex_basis),
  };
};

/**
 * THE CHIP THAT SAYS WHY A PRESENT ANIMAL IS ON ITS WAY OUT, or null.
 *
 * S3-05.3, operator ruling 2026-09-26: *"Animals that have been sold but
 * are holding pending shipping, yes, I want to see those and maybe they
 * should have a different thing."* The racks page draws what is
 * physically in the tank (`archiving.PRESENT_ANIMAL` — not deceased, in
 * possession), and a SOLD animal qualifies: the keeper walks past it and
 * feeds it. It is not an ordinary resident either, so it is labelled.
 *
 * ONE implementation, because the desktop drawer and the phone occupant
 * list must say the same words about the same animal — the
 * `InactiveClutches` lesson, which existed as one component for exactly
 * this reason.
 *
 * SOLD OUTRANKS ON HOLD and the data agrees: the Rule-2 cutover
 * (2026-08-05, #32) clears `is_on_hold` when an animal is marked sold, so
 * the two are mutually exclusive on any row written since — the order
 * here is what a legacy row carrying both reads as, and "sold" is the
 * stronger fact about it.
 *
 * It is STATIC TEXT, not a control, so there is no 44px floor to meet.
 */
export const presenceChip = (animal) => {
  if (!animal) return null;
  if (animal.is_sold) {
    return { label: 'Sold \u00b7 awaiting shipment', variant: 'warning' };
  }
  if (animal.is_on_hold) return { label: 'On hold', variant: 'info' };
  return null;
};

/**
 * THE PRESENCE MARKER ON A CLOSED CARD'S FACE — `' · 1 sold · 1 on hold'`,
 * or `''`.
 *
 * S3-05.3 fix pass 1, item 4. `presenceChip` above labels ONE ROW in an
 * open drawer; a keeper walking the room reads the FACE and may never open
 * it, so the same fact has to be on the closed card and on the closed
 * phone row. The two numbers are the SERVER'S, sent beside `animals_count`
 * from `archiving.present_animal_counts` — the card cannot count them
 * itself, because a tank's animal rows are fetched when its drawer opens.
 *
 * ⚠️ ONE IMPLEMENTATION, and it is the SAME RULE as `presenceChip`,
 * including its precedence: a row that is both sold and on hold is counted
 * as SOLD only, because that is the single word its chip says. The
 * exclusion lives in the server helper; this reads what it sent. A second
 * derivation here is how "the face says 2, the drawer names 1" starts.
 */
export const presenceFaceText = (tank) => {
  const sold = Number(tank?.animals_sold_count) || 0;
  const onHold = Number(tank?.animals_on_hold_count) || 0;
  const parts = [];
  if (sold > 0) parts.push(`${sold} sold`);
  if (onHold > 0) parts.push(`${onHold} on hold`);
  return parts.length ? ` \u00b7 ${parts.join(' \u00b7 ')}` : '';
};

export const clutchLabel = (clutch) => {
  const speciesName =
    clutch.species_name ||
    clutch.species?.common_name ||
    formatSpeciesName(clutch.species, { withLocality: false }) ||
    'Unknown Species';
  const count = Number(clutch.current_count ?? clutch.count ?? clutch.quantity ?? 0);
  const age = clutch.age_display ?? clutch.age ?? '';
  return {
    speciesName,
    count,
    age,
    stage: clutch.stage,
    name: clutch.name || speciesName,
    // S3-05.3 review round 2 — "NOT COUNTED YET" IS NOT "ZERO LEFT".
    // `current_count` is 0 for a clutch counted DOWN to zero and 0 for one
    // nobody has counted yet, and the second must not read "0 eggs": the
    // Add Clutch modal with a blank count writes exactly that row, and so
    // does a lay with no `egg_count` (the E5 rule). The server says which
    // (`has_count_row`); an ABSENT flag reads as counted, so an older
    // backend's rows print exactly what they printed before.
    counted: clutch.has_count_row !== false,
  };
};

/** What a count line says when nobody has counted the clutch yet. */
export const NOT_COUNTED_YET = 'not counted yet';

/**
 * ⚠️ **THE DRAWER NAMES THE CLUTCH** (Cowork run 25, F9). A clutch row in
 * "Who's inside" read "Agalychnis Callidryas — Age: 2d — not counted
 * yet**eggs**": no clutch name at all, and the stage badge glued to the
 * count with no space. ONE derivation for both drawers (desktop `TankBox`
 * and `phone/PhoneRackDetail`): the row LEADS with the clutch's own name,
 * then the species (only when a name was there to lead — an unnamed
 * clutch's title already IS its species), then the age, then the count —
 * "not counted yet" when nobody has counted it (S3-05.3 r2).
 */
const CLUTCH_EGG_STAGES = new Set(['egg', 'eggs']);

export const clutchRowParts = (clutch) => {
  const info = clutchLabel(clutch);
  const named = !!String(clutch?.name || '').trim();
  // ⚠️ THE COUNT SAYS ITS STAGE (Cowork run 34, E5). "Age: 10d — not
  // counted yet eggs" was the count and a stage badge read as one phrase;
  // it reads "eggs, not counted yet" now, and a counted clutch "12 eggs" /
  // "3 tadpoles". A stage we have no noun for keeps "Count: N" and its
  // badge, exactly as before.
  // ⚠️ THE LEDGER LINE IS THE COUNT (the leftovers batch, item 19 —
  // F3/F5): Cowork read "5 froglets" (the total named by the coarse stage)
  // beside a ledger of 4 eggs · 0 tadpoles · 1 froglet, and a found clutch
  // "1 egg" beside 0 eggs · 1 tadpole. The server's `ledger_line` is the
  // phrase whenever the clutch has ledger rows; the old phrase only when it
  // has none (null).
  const ledgerLine = typeof clutch?.ledger_line === 'string' && clutch.ledger_line
    ? clutch.ledger_line : null;
  const phrase = ledgerLine || clutchStageCountText(info);
  const ageText = info.age ? `Age: ${info.age}` : null;
  const detail = [
    named ? info.speciesName : null,
    ageText,
    phrase || (info.counted ? `Count: ${info.count}` : NOT_COUNTED_YET),
  ].filter(Boolean);
  // Reptile age bands (2026-10-14): a LISTED reptile clutch past the egg
  // carries the server's band word (`stage_label`: "Juvenile" /
  // "Subadult" / "Adult") — the stage the planner buckets it under and
  // writes its tub rows with — so the badge says it beside the count. The
  // server sends null for every other clutch, which keeps exactly the
  // badge it had (none behind a count phrase, else the coarse stage). An
  // egg-stage frog/aquatic clutch's "Eggs" would only repeat "3 eggs", so
  // it is not drawn.
  //
  // ⚠️ A REPTILE CLUTCH STILL AT THE EGG says "Egg" (Cowork run 52, C;
  // operator ruling 2026-10-17 — a reptile with no hatch date is an egg at
  // any age): its row had NO badge while an egg-stage gecko animal's said
  // "Egg", so the keeper could not tell a months-old egg from a clutch the
  // page had nothing to say about. The gecko ladder's egg rung is `egg`
  // (frogs and aquatics say `eggs`), which is exactly the reptile set.
  const coarse = String(info.stage || '').trim().toLowerCase();
  // Beside a ledger line the coarse stage says nothing the line does not
  // (and "Egg" beside "0 eggs · 1 hatchling" contradicts it); a listed
  // reptile's band word is the LISTING stage and stays.
  const bandWord = (coarse === 'egg' && !ledgerLine) ? 'Egg'
    : (CLUTCH_EGG_STAGES.has(coarse) || coarse === 'egg'
      ? null : stageWordFor(clutch));
  return {
    title: info.name,
    detail,
    stage: bandWord || (phrase ? null : info.stage),
    ageText,
    ageTitle: ageText ? drawerAgeTitle(clutch, 'clutch') : null,
  };
};

/**
 * The noun a clutch's count is said in, by its stage — a declared PORT of
 * `facility/services/feeding_occupancy.STAGE_COUNT_NOUNS` (the feeding
 * card's "1 clutch (12 tadpoles)"), so the drawer and the card name one
 * clutch the same way.
 */
const STAGE_COUNT_NOUNS = {
  eggs: ['egg', 'eggs'],
  egg: ['egg', 'eggs'],
  tadpole: ['tadpole', 'tadpoles'],
  terrestrial: ['froglet', 'froglets'],
  hatched: ['hatchling', 'hatchlings'],
};

/**
 * "12 eggs" / "1 tadpole" / "eggs, not counted yet" — or null for a stage
 * with no noun (the caller keeps its old words).
 */
export const clutchStageCountText = (info) => {
  const nouns = STAGE_COUNT_NOUNS[String(info?.stage || '').trim().toLowerCase()];
  if (!nouns) return null;
  if (!info.counted) return `${nouns[1]}, ${NOT_COUNTED_YET}`;
  return `${info.count} ${info.count === 1 ? nouns[0] : nouns[1]}`;
};

/**
 * ⚠️ **ONE NUMBER, NEVER A COUNT OVER A HIDDEN LIST** (Cowork run 25, F9).
 * "Animals (21)" sat over a 220px scroll box that showed four rows. The
 * drawer lists at most `DRAWER_ROWS_SHOWN` rows until "Show all" is
 * pressed, and the head says how many are shown of how many.
 */
export const DRAWER_ROWS_SHOWN = 5;

export const drawerListHead = (label, shown, total) => (
  shown >= total ? `${label} (${total})` : `${label} — showing ${shown} of ${total}`);

/** "3 eggs" / "1 egg" / "not counted yet" — ONE derivation, every surface. */
export const clutchCountText = (info) => {
  if (!info.counted) return NOT_COUNTED_YET;
  return `${info.count} egg${info.count === 1 ? '' : 's'}`;
};

/** "2026-08-29" -> "8/29" — a cup is 120px wide (review of 1c80c288). */
export const monthDay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${Number(m[2])}/${Number(m[3])}` : String(iso || '');
};

/**
 * AN EGG CUP'S COUNT AND DATE, before and after its hatch (Cowork re-check
 * 2026-10-28: a cup read "2 eggs · laid 8/29" after its Hatch was logged).
 *
 * The signal is the clutch's own `hatch_date` (a logged Hatch sets it).
 * Unhatched: "2 eggs" / "not counted yet", "laid 8/29". Hatched, the count
 * is said in the stage's noun ("2 hatchlings", "3 tadpoles" — the drawer's
 * ONE noun table), or "2 hatched" for a stage with no noun or one still
 * reading egg, and the date is the hatch: "hatched 10/24". A cup nobody
 * counted says "not counted yet" either way.
 *
 * ⚠️ TWO PARTS, TWO ROWS (review of 1c80c288, measured in Chromium): a
 * shelved cup is a 120px box (110px of text), and "2 hatchlings · hatched
 * 10/24" is 160px on one line — so CupChip draws the count and the date on
 * rows of their own, and the dates are M/D.
 */
export const cupLineParts = (cup) => {
  const info = clutchLabel(cup);
  if (!cup?.hatch_date) {
    return {
      count: clutchCountText(info),
      // R-OBLIGATE OB-1: a found clutch (no lay seen) says so.
      date: cup?.birth_date ? `laid ${monthDay(cup.birth_date)}`
        : (isLayNotSeen(cup) ? LAY_NOT_SEEN : ''),
    };
  }
  let count = NOT_COUNTED_YET;
  if (info.counted) {
    const stage = String(info.stage || '').trim().toLowerCase();
    count = (CLUTCH_EGG_STAGES.has(stage) ? null : clutchStageCountText(info))
      || `${info.count} hatched`;
  }
  return { count, date: `hatched ${monthDay(cup.hatch_date)}` };
};

/** The cup's two parts as one phrase: "2 hatchlings · hatched 10/24". */
export const cupLineText = (cup) => {
  const { count, date } = cupLineParts(cup);
  return date ? `${count} · ${date}` : count;
};

/**
 * WHAT A LEFTOVER SLOT IS, AND HOW ITS RECORD COMES BACK — said once.
 *
 * S3-05.3 review round 2, item 4 (NIT). `SET_ASIDE_NOTICE` was ONE string
 * for FOUR shapes and described one of them: "empty records are not shown
 * on this page … under Empty/split — Shown" is untrue of a DECEASED or
 * SHIPPED-OUT cup carrying a real count, twice over — it is not empty, and
 * `is_split=False, latest_count__gt=0` means the Clutches page's DEFAULT
 * list already shows it. It also named no way back, and the Move dialog
 * cannot reach an inactive cup (item 3: **Remove is the only path off the
 * shelf for a leftover**), so re-placing really does need a chip cleared
 * on the Clutches grid first.
 *
 * So the sentence is derived from the row's own flags, and the shape word
 * the placeholder prints comes from the SAME call — two surfaces cannot
 * describe one slot differently.
 *
 * ⚠️ NOTHING CLAIMS "EMPTY" ABOUT A ROW WITH A REAL COUNT. "Empty cup" is
 * round 1's decided copy and is kept for the row it was written about —
 * the one counted to zero; the other three say what they are.
 *
 * Precedence is the flag the OPERATOR set by hand first (deceased, then
 * shipped out, then split), because that is the one they can undo; the
 * count is the last resort. A row we cannot classify claims nothing.
 *
 * ⚠️ AND THE SENTENCE MUST NOT SEND THE OPERATOR TO A SCREEN THAT HIDES THE
 * ROW (review round 3, item 2). The Clutches page's DEFAULT list is
 * `is_split=False, latest_count__gt=0`, so a deceased or shipped-out cup is
 * on it only while it still carries a real count — and the commonest
 * sequence there is (a clutch that finished at 0 and was THEN marked
 * deceased, or one nobody ever counted) is exactly the one "It is on the
 * Clutches page" leads to an empty screen for. So the DECEASED and
 * SHIPPED-OUT sentences name the opt-in too whenever the row would be
 * hidden — count ≤ 0, no count row at all, or split — and keep the plain
 * sentence when the row really is in the default list. The chip to clear is
 * still named either way: that is the way back, and the opt-in is only how
 * to SEE the row while the chip is set.
 */
const hiddenFromTheDefaultList = (cup) => (
  cup?.is_split === true
  // ⚠️ `latest_count` COALESCEs "never counted" to 0, so the default list
  // hides an uncounted row exactly as it hides one counted down to zero —
  // the same fact `clutchLabel` reads as `counted`, read here for a
  // different question. An ABSENT flag is a count row, as everywhere else.
  || cup?.has_count_row === false
  || (Number(cup?.current_count ?? cup?.count ?? 0) || 0) <= 0
);

export const cupLeftover = (cup) => {
  const onTheGrid = 'It is on the Clutches page';
  const underEmptySplit =
    `Find it on the Clutches page under "${EMPTY_SHOWN_OPTION}".`;
  const where = hiddenFromTheDefaultList(cup)
    ? `${onTheGrid} under "${EMPTY_SHOWN_OPTION}"`
    : onTheGrid;
  if (cup?.is_deceased === true) {
    return {
      label: 'Deceased cup',
      notice: `Marked deceased \u2014 ${where}; clear the Deceased chip `
        + 'there to place it again.',
    };
  }
  if (cup?.is_in_possession === false) {
    return {
      label: 'Shipped-out cup',
      notice: `Shipped out \u2014 ${where}; set In possession there to `
        + 'place it again.',
    };
  }
  if (cup?.is_split === true) {
    return {
      label: 'Split cup',
      notice: `Split \u2014 ${underEmptySplit}`,
    };
  }
  if ((Number(cup?.current_count ?? cup?.count ?? 0) || 0) <= 0) {
    return {
      label: 'Empty cup',
      notice: `Emptied \u2014 ${underEmptySplit}`,
    };
  }
  // Unreachable from `present=true` (the server only marks those four
  // shapes inactive) — and it claims nothing rather than guessing.
  return {
    label: 'Cup slot',
    notice: `Set aside \u2014 ${onTheGrid}.`,
  };
};

/**
 * WHAT IS IN A TANK, DESCRIBED ONCE — both destructive confirms read the
 * SAME server answer (``GET enclosures/<id>/archive/``), so they may not
 * describe one row differently.
 *
 * S3-05.3 review round 5, item 3 (NIT). The ARCHIVE dialog guarded the
 * trailing marker on ``has_count_row === false`` and the DELETE confirm
 * guarded it on ``present``, so a clutch with no count row that was ALSO
 * marked deceased — reachable: blank-count Add Clutch, marked deceased
 * later — read `Dead fresh cup (deceased) — deceased` in the delete
 * confirm and `Dead fresh cup (deceased)` in the archive dialog, about
 * one row and one server answer.
 *
 * ⚠️ THE BRACKETS AND THE MARKER ANSWER DIFFERENT QUESTIONS. The brackets
 * say what the row IS — the server's own presence word
 * (``archiving.group_presence_word``) for a row nobody has counted, its
 * count otherwise. The marker says what THIS tank's decision does with
 * it, which is why a row that has already named itself in the brackets
 * takes none.
 */
export const insideRowText = (c) => (
  c?.has_count_row === false
    ? `${c?.name} (${c?.state || NOT_COUNTED_YET})`
    : `${c?.name} (${c?.count})`);

/**
 * The marker a BLOCKER carries — the sweep's own word for the same fact
 * (``archiving._blocked_phrase``: "Bruce is still in it").
 */
export const STILL_IN_IT = ' — still in it';

/**
 * The trailing marker on one clutch row.
 *
 * ``blockedNote`` is the CALLER'S, because the two decisions genuinely
 * differ and neither may borrow the other's: the archive's checkbox MOVES
 * a counted clutch, so it is not refused there and says nothing; the
 * DELETE has no checkbox and is refused by it (409 ``enclosure_occupied``,
 * ``views_racks.delete_enclosure``).
 */
export const insideClutchNote = (c, blockedNote = '') => {
  if (c?.blocks) return blockedNote;
  // An uncounted row has already said what it is, in the brackets.
  if (c?.has_count_row === false) return '';
  // Otherwise the BLOCKING rule's own sentence stands — it is what says
  // "being set aside" and "deceased — does not block".
  return ` — ${c?.reason}`;
};

/** The same, for an animal row: there is no `present` on one — a SOLD
 *  animal is present and does not block, so reading one off the other
 *  would be a lie. */
export const insideAnimalNote = (a, blockedNote = '') => (
  a?.blocks ? blockedNote : ` — ${a?.reason}`);

/** Every row the occupancy rule refuses, animals first — ONE reading. */
export const blockingRowsIn = (counts) => ([
  ...((counts && counts.animals) || []).filter((a) => a?.blocks),
  ...((counts && counts.clutches) || []).filter((c) => c?.blocks),
]);

/** How many names a refusal reads out before it starts counting. */
export const BLOCKER_NAME_LIMIT = 5;

/**
 * "Bruce", "Bruce and Six cup", "A, B, C, D, E and 3 more" — the PORT of
 * ``archiving._blocked_phrase``'s grammar, checked at both ends, so the
 * sweep's skipped list and a confirm dialog name one set the same way.
 */
export const blockerNames = (rows, total = undefined) => {
  const real = (rows || []).map((r) => r?.name).filter(Boolean);
  if (!real.length) return '';
  // `total` is the WHOLE-SET count when the caller knows it (the delete
  // refusal: rows past INSIDE_LIMIT are unseen), so "and N more" is
  // counted ONCE; every other caller leaves it at the rows themselves.
  const whole = Math.max(Number(total) || 0, real.length);
  if (whole === 1) return real[0];
  const shown = real.slice(0, BLOCKER_NAME_LIMIT);
  const more = whole - shown.length;
  const items = more ? shown.concat([`${more} more`]) : shown;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

/**
 * THE REFUSAL THE DELETE WOULD OTHERWISE BE — ``null`` when nothing
 * blocks (S3-05.3 review round 5, item 1 — SHOULD).
 *
 * The new confirm promised "Deleting the tank leaves these records behind
 * unplaced", which the server refuses outright while anything living is
 * inside: a live animal or a counted clutch is a 409 ``enclosure_occupied``
 * (``views_racks.py``), and the operator read the promise, pressed the
 * button and found out afterwards. The blockers were the rows with NO
 * marker, because only the non-blocking ones were annotated.
 *
 * So the sentence names them in the archive dialog's own words and says
 * the server's own way out ("Move its animals and clutches out first."),
 * and "Really delete" is DISABLED while one stands — a button that cannot
 * succeed is not offered.
 */
export const blockedDeleteRefusal = (counts) => {
  // THE GATE IS THE WHOLE-SET COUNT, NEVER THE CAPPED LIST (verification
  // of round 5): `animals`/`clutches` are sliced at INSIDE_LIMIT (20) and
  // ordered by name, so a live animal past the cap is absent from the
  // rows while `blocking_animals` still counts it — CLAUDE.md's own rule
  // for why the `blocking_*` numbers exist. The rows only NAME.
  const blocking = (Number(counts?.blocking_animals) || 0)
    + (Number(counts?.blocking_clutches) || 0);
  const rows = blockingRowsIn(counts);
  if (!blocking && !rows.length) return null;
  const total = Math.max(blocking, rows.length);
  const names = blockerNames(rows, total);
  const subject = names
    || (total === 1 ? 'A living record' : `${total} living records`);
  return `${subject} ${total === 1 ? 'is' : 'are'} `
    + 'still in this tank, so it cannot be deleted — move its animals '
    + 'and clutches out first.';
};

export const dimsLabel = (thing) => {
  const parts = [thing?.length, thing?.width, thing?.height]
    .map((d) => (d === null || d === undefined || d === '' ? null : Number(d)))
    .filter((d) => d !== null && Number.isFinite(d));
  return parts.length === 3 ? `${parts[0]}×${parts[1]}×${parts[2]}"` : null;
};

/** "~12 in free", or a bare "free" when a dimension is missing. */
export const freeLabel = (spanFree) =>
  (spanFree === null || spanFree === undefined
    ? 'free'
    : `~${Math.round(spanFree * 10) / 10} in free`);

/** NIT 14 — an over-stuffed shelf says so instead of claiming "~0 in free". */
export const overLabel = (spanOver) =>
  (spanOver === null || spanOver === undefined
    ? null
    : `over by ${Math.round(spanOver * 10) / 10} in`);

/** Egg cups belong on any shelf; incubator racks just lead with them. */
export const isIncubatorKind = (kind) =>
  String(kind || '').toLowerCase().includes('incubator');

// ---------------------------------------------------------------------
// Drawing scale (re-composed 2026-09-02, operator eye test).
//
// The six-racks-side-by-side board is gone: one rack fills the page, so
// the pixels-per-inch budget is the CONTENT width, not a ~300px column.
// A 77" rack across 1100px draws at ~14.3 px/in — the resemblance the
// operator asked for. The old 4 px/in ceiling was a symptom of the
// column layout, not a drawing decision.
//
// Widths are still set INLINE (a stylesheet floor beat every computed
// width once already, Cowork finding 2) and the row is allowed to scroll
// inside itself when the legibility FLOOR forces it. The page body never
// scrolls sideways.
// ---------------------------------------------------------------------

/** Assumed content width before/without a live measurement (jsdom, first
 *  paint). A full-width rack detail on a laptop. */
export const DEFAULT_CONTENT_WIDTH = 1100;

/** Hard ceiling: past this a small rack would be drawn absurdly large. */
export const MAX_PPI = 16;
/** Hard floor: below this an 18" tank stops being readable at all. */
export const MIN_PPI = 3;

/** Smallest a to-scale box may be drawn, whatever the arithmetic says. */
export const SCALE_MIN_TANK_PX = 48;
/** Below this width a box cannot carry its action row; it collapses. */
export const COMPACT_BELOW_PX = 140;
/** Narrower than this and the ghost "+ tank" tile cannot be clicked. */
export const GHOST_MIN_PX = 56;
/** Narrower than this and "~N in free" cannot be read; it hides. */
export const FREE_LABEL_MIN_PX = 40;

// BLOCKER 1+3 (Opus review): span x ppi is NOT what a row lays out. Every
// tank is preceded by an 8px `.rk-gap` drop target and the row itself has
// a 2px flex gap between children — 48px of chrome on a four-tank shelf.
// Budgeting the drawing against the raw row width therefore asked for
// 1148px inside 1100px: the free region and its ghost scrolled off, and
// the page picked up ~10px of horizontal scroll. Chrome is now subtracted
// BEFORE the scale is chosen, and the free region absorbs the remainder
// so a row's parts sum to exactly the width it has.

/** `.rk-gap` — the drop target rendered before every tank. */
export const TANK_GAP_PX = 8;
/** `.rk-shelf__row`'s own `gap`, applied between every pair of children. */
export const ROW_FLEX_GAP_PX = 2;
/** A tank with no length falls back to this floor (the CSS token). */
export const UNIFORM_MIN_TANK_PX = 96;
/** An egg cup is content-sized text, so the row pins it to a known box. */
export const CUP_WIDTH_PX = 120;
/** Width the "over by N in" warning chip needs to stay readable. */
export const FREE_WARNING_PX = 120;

/** Pixels one shelf row spends on chrome rather than on drawing. */
export const shelfChromePx = (shelf, { withFree = true } = {}) => {
  const tanks = (shelf?.tanks || []).length;
  // S3-05.3 fix pass 1: BOTH halves of the placed cups take a box in the
  // row, so both are chrome the width budget has to know about — a
  // placeholder is counted in no COUNT the operator reads, and in every
  // LAYOUT number, because it is physically drawn.
  const cups = (shelf?.placedCups
    || [...(shelf?.cups || []), ...(shelf?.cupPlaceholders || [])]).length;
  // Each tank contributes TWO children: its drop gap, then the box.
  const children = tanks * 2 + cups + (withFree ? 1 : 0);
  return tanks * TANK_GAP_PX + Math.max(0, children - 1) * ROW_FLEX_GAP_PX;
};

/**
 * Pixels per inch from a width that is already free of chrome. Kept as
 * the primitive so the clamp lives in exactly one place.
 */
export const computePpi = (usableWidth, spanInches) => {
  const width = Number(usableWidth) > 0
    ? Number(usableWidth) : DEFAULT_CONTENT_WIDTH;
  const span = Number(spanInches);
  if (!Number.isFinite(span) || span <= 0) return MAX_PPI;
  return Math.max(MIN_PPI, Math.min(MAX_PPI, width / span));
};

/**
 * ONE scale for the whole rack — the largest pixels-per-inch at which
 * EVERY shelf's tanks plus its chrome still fit the row it is given. The
 * widest shelf is not always the tightest: a six-tank shelf spends 72px
 * on chrome where a two-tank shelf of the same span spends 24px.
 */
export const computeRackPpi = (rowContentWidth, rack) => {
  const width = Number(rowContentWidth) > 0
    ? Number(rowContentWidth) : DEFAULT_CONTENT_WIDTH;
  const shelves = (rack?.faces || [])
    .flatMap((face) => face.shelves || [])
    .filter((shelf) => Number(shelf.span) > 0);
  if (!shelves.length) return MAX_PPI;
  const tightest = shelves.reduce((worst, shelf) => {
    const fit = Math.max(0, width - shelfChromePx(shelf)) / Number(shelf.span);
    return worst === null || fit < worst.fit ? { shelf, fit } : worst;
  }, null);
  return computePpi(
    Math.max(0, width - shelfChromePx(tightest.shelf)),
    Number(tightest.shelf.span),
  );
};

/** The widest shelf span on a rack, across every face. Null if unknown. */
export const widestSpan = (rack) => {
  const spans = (rack.faces || [])
    .flatMap((face) => face.shelves || [])
    .map((shelf) => Number(shelf.span))
    .filter((n) => Number.isFinite(n) && n > 0);
  return spans.length ? Math.max(...spans) : null;
};

/**
 * The hardware properties a rack has, as `{ key, label }`.
 *
 * The ROOM TILE draws them as icons with the word on hover (the tile has
 * to read at a glance, and three word-chips crowded out the
 * silhouette); the rack DETAIL page draws icon AND word, because it has
 * the room. One list, two renderings — and no emoji in either: the
 * flame used to be a literal 🔥, which renders differently on every
 * platform and cannot take a token colour.
 */
export const rackPropertyIcons = (rack) => [
  rack?.is_heated ? { key: 'heated', label: 'heated' } : null,
  rack?.is_rolling ? { key: 'rolling', label: 'rolling' } : null,
  rack?.is_two_sided ? { key: 'two-sided', label: 'two-sided' } : null,
].filter(Boolean);

// ---------------------------------------------------------------------
// The room map (operator eye test, 2026-09-02).
//
// "Maybe here's a map and you click on the rack." The map is drawn from
// data that ALREADY EXISTS — the free-text `Rack.location` — by looking
// for a wall keyword in it. No new fields, no new admin: a rack whose
// location says nothing recognisable lands in "Elsewhere" and is still
// one click from its detail.
// ---------------------------------------------------------------------

/** The nine cells of the room rectangle, plus the honest overflow. */
export const ROOM_ZONES = [
  'northwest', 'north', 'northeast',
  'west', 'center', 'east',
  'southwest', 'south', 'southeast',
  'elsewhere',
];

// Title case because these are now WRITTEN INTO `Rack.location` when a
// tile is dragged to a wall — they are proposed data, not just captions.
export const ZONE_LABELS = {
  northwest: 'North-West Corner',
  north: 'North Wall',
  northeast: 'North-East Corner',
  west: 'West Wall',
  center: 'Middle of the Room',
  east: 'East Wall',
  southwest: 'South-West Corner',
  south: 'South Wall',
  southeast: 'South-East Corner',
  elsewhere: 'Elsewhere',
};

/**
 * The zone, said mid-sentence. ZONE_LABELS is Title Case because it is
 * written INTO the location field; read back inside a sentence it has to
 * be prose again.
 */
export const zonePhrase = (zone) => {
  if (zone === 'elsewhere') return 'not placed on the room map';
  if (zone === 'center') return 'in the middle of the room on the map';
  return `on the ${(ZONE_LABELS[zone] || '').toLowerCase()} of the room map`;
};

/**
 * The location text a zone proposes. "Elsewhere" is the absence of a
 * keyword, so it proposes NOTHING and the operator types (or leaves
 * empty) whatever is true.
 */
export const zoneLocationText = (zone) =>
  (zone === 'elsewhere' ? '' : (ZONE_LABELS[zone] || ''));

/** Shelf rows a tile silhouette draws before it says "+N". */
export const SILHOUETTE_ROWS = 6;

/** A shelf with the least headroom, and one with the most. */
export const SILHOUETTE_ROW_MIN_PX = 7;
export const SILHOUETTE_ROW_MAX_PX = 16;

/**
 * The rack drawn small, but drawn HONESTLY (operator eye test: "racks
 * look too small… hard to tell what it is").
 *
 * Each shelf becomes a row whose HEIGHT tracks its clearance relative to
 * the tallest shelf on the same rack, and whose blocks are as wide a
 * share of the row as the tank is of the shelf's span. So a rack with a
 * tall bottom shelf and shallow top shelves looks like that rack, not
 * like every other rack.
 *
 * Missing dimensions degrade rather than lie: a shelf with no clearance
 * gets the middle height, and a tank with no length shares whatever
 * width is left with the other unknowns (`flex: 1 1 0`).
 */
export const silhouetteRows = (rack) => {
  const shelves = (rack?.faces || []).flatMap(
    (face) => (face.shelves || []).map((shelf) => ({ face: face.face, shelf })));

  const clearances = shelves
    .map(({ shelf }) => Number(shelf.clearance))
    .filter((n) => Number.isFinite(n) && n > 0);
  const tallest = clearances.length ? Math.max(...clearances) : null;
  const midHeight = Math.round(
    (SILHOUETTE_ROW_MIN_PX + SILHOUETTE_ROW_MAX_PX) / 2);

  const rows = shelves.slice(0, SILHOUETTE_ROWS).map(({ face, shelf }) => {
    const span = Number(shelf.span);
    const usable = Number.isFinite(span) && span > 0 ? span : null;
    const clearance = Number(shelf.clearance);
    const height = tallest && Number.isFinite(clearance) && clearance > 0
      ? Math.round(
        SILHOUETTE_ROW_MIN_PX
          + (clearance / tallest) * (SILHOUETTE_ROW_MAX_PX - SILHOUETTE_ROW_MIN_PX))
      : midHeight;
    return {
      key: `${face || 'single'}-${shelf.id}`,
      height,
      blocks: (shelf.tanks || []).map((tank) => {
        const length = Number(tank.length);
        // OPERATOR EYE TEST 2026-09-04: blocks used to be sized in
        // PERCENT of the shelf span with `flex: 0 0 auto`, so a rack
        // with six tanks on a row drew ~110px past the tile's right
        // edge. A share of the ROW is what a silhouette wants: grow
        // proportional to length, basis 0, so any number of tanks fits
        // the row exactly whatever the tile's width turns out to be.
        const grow = usable && Number.isFinite(length) && length > 0
          ? Math.max(0.01, length / usable)
          : null;
        return { key: String(tank.id), grow };
      }),
    };
  });

  return { rows, more: Math.max(0, shelves.length - rows.length) };
};

// ---------------------------------------------------------------------
// The drag/drop CONTRACT.
//
// Cowork run 3: a drag activated (ghost + is-over both seen) and then
// committed NOTHING. The shapes the components hand `useDraggable` /
// `useDroppable` and the shapes the resolvers below read are the same
// contract, and nothing was holding them together — so they are built
// HERE, by these four functions, used by the components, and asserted
// by the tests. A test that hand-writes an `active`/`over` shape proves
// only that the test agrees with itself.
// ---------------------------------------------------------------------

/** What a rack tile registers as, and what the resolvers read back. */
export const rackDragData = (rack) => ({ kind: 'rack', id: String(rack.id) });

/** Ditto for a freestanding tank tile. */
export const tankDragData = (tank) => ({ kind: 'tank', id: String(tank.id) });

/**
 * A room card's drop payload. A PLACEHOLDER room (no row behind it —
 * the fallback main room) can be looked at but never moved into, so it
 * deliberately carries no `roomId` and resolves to nothing.
 */
export const roomDropData = (room) => (
  room?.id === null || room?.id === undefined
    ? { kind: 'roomPlaceholder' }
    : { kind: 'room', roomId: String(room.id) });

/** One cell of the room plan. */
export const zoneDropData = (zone) => ({ kind: 'zone', zone });

/**
 * A rack OR a freestanding tank landing on a zone of the room plan.
 * Returns `{ kind, id, zone }`, or null when the drop means nothing.
 *
 * S3-02.3b: freestanding tanks are placeable too ("we should be able to
 * specify where the individual or freestanding racks are"), and they
 * carry the same wall keyword racks do — in `Enclosure.location`.
 */
export const resolveZoneDrop = (active, over) => {
  const subject = active?.data?.current;
  const target = over?.data?.current;
  if (!subject || (subject.kind !== 'rack' && subject.kind !== 'tank')) return null;
  if (!target || target.kind !== 'zone') return null;
  return { kind: subject.kind, id: String(subject.id), zone: target.zone };
};

/** What a drag or a Wall… pick resolves to, or null if it means nothing. */
export const resolveWallDrop = (active, over) => {
  const hit = resolveZoneDrop(active, over);
  return hit && hit.kind === 'rack'
    ? { rackId: hit.id, zone: hit.zone } : null;
};

/**
 * A rack or freestanding tank dropped ON ANOTHER ROOM's rectangle.
 * Returns `{ kind, id, roomId }` — the board turns it into a confirm,
 * never a silent write, because it moves the thing out of the room the
 * operator is looking at.
 */
export const resolveRoomDrop = (active, over) => {
  const subject = active?.data?.current;
  const target = over?.data?.current;
  if (!subject || (subject.kind !== 'rack' && subject.kind !== 'tank')) return null;
  if (!target || target.kind !== 'room' || !target.roomId) return null;
  return { kind: subject.kind, id: String(subject.id), roomId: String(target.roomId) };
};

// NIT 10 (Opus review): substring matching read a wall out of any word
// that merely contained one — "Northampton shelf" became the north wall,
// "Westinghouse freezer" the west. Matching is on WORD boundaries, and
// the compound corner spellings are tried first so "southeast" is a
// corner rather than the south wall.
const CORNER_PATTERNS = [
  ['northeast', /\bnorth[\s-]?east\b/i],
  ['northwest', /\bnorth[\s-]?west\b/i],
  ['southeast', /\bsouth[\s-]?east\b/i],
  ['southwest', /\bsouth[\s-]?west\b/i],
];

/**
 * Which cell of the room a rack sits in, from the wall keyword in its
 * location text. Corners win over single walls, and anything
 * unrecognised is "elsewhere" rather than a guess.
 */
export const wallZoneFor = (location) => {
  const text = String(location || '');
  const corner = CORNER_PATTERNS.find(([, re]) => re.test(text));
  if (corner) return corner[0];

  const north = /\bnorth\b/i.test(text);
  const south = /\bsouth\b/i.test(text);
  const east = /\beast\b/i.test(text);
  const west = /\bwest\b/i.test(text);
  if ((north || south) && (east || west)) {
    return `${north ? 'north' : 'south'}${east ? 'east' : 'west'}`;
  }
  if (north) return 'north';
  if (south) return 'south';
  if (east) return 'east';
  if (west) return 'west';
  if (/\b(middle|center|centre)\b/i.test(text)) return 'center';
  return 'elsewhere';
};

/** Does this tank answer the overview/detail search box? */
export const tankMatchesQuery = (tank, query) => {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return false;
  const hay = `${tank?.name || ''} ${tank?.position_label || ''}`.toLowerCase();
  return hay.includes(needle);
};

// ---------------------------------------------------------------------
// SHELF ORDER IS NOT STORED ANYWHERE, AND THERE IS NOTHING TO READ.
//
// Operator ruling 2026-09-15 (evening), reversing 2026-09-14: SHELF 1 IS
// THE BOTTOM and the highest number is the TOP — every rack was set up
// bottom-up and the tank names already encode it, so the app flipped and
// no data moved. A FIXED CONVENTION, not a setting,
// so `readShelfOrder` / `writeShelfOrder` and the `ff.racks.shelfOrder`
// localStorage key are gone. They were PER BROWSER PER RACK, which meant
// a phone and a desktop could draw ONE rack in opposite directions while
// the printed labels, the round's walking order and FroggerAI all read
// the bare `shelf_no`. Both views now render `face.shelves` in the order
// `useRackTree` sorted them — DESCENDING `shelf_no`, because shelf 1 is
// the BOTTOM (operator ruling 2026-09-15 evening).
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// Rooms (operator ruling, 2026-09-02).
//
// "Add second or third rooms relative to the main room… my bug room
// should be in the lower left hand corner… support rooms, not multiple
// levels." One level: a MAIN room drawn as the plan, and support rooms
// drawn as small rectangles AROUND it, each placed by the wall/corner
// keyword in `Room.position` — parsed by the very same `wallZoneFor`
// that already reads `Rack.location`, so the operator learns one
// vocabulary, not two.
// ---------------------------------------------------------------------

/** The eight cells a support room can occupy around the main room. */
export const ROOM_POSITION_ZONES = [
  'northwest', 'north', 'northeast',
  'west', 'east',
  'southwest', 'south', 'southeast',
];

/**
 * The text a position keyword proposes. Deliberately NOT `ZONE_LABELS`:
 * a support room is not on the main room's north WALL, it is to the
 * north OF it. Every one of these still parses back through
 * `wallZoneFor`, so the map reads what the operator wrote.
 */
export const ROOM_POSITION_LABELS = {
  northwest: 'North-West',
  north: 'North',
  northeast: 'North-East',
  west: 'West',
  east: 'East',
  southwest: 'South-West',
  south: 'South',
  southeast: 'South-East',
};

/**
 * Which cell a support room draws in, or null when its position names
 * nothing the map understands — those go to "Other rooms" below the
 * plan rather than being guessed onto a side.
 */
export const roomPositionZone = (position) => {
  const zone = wallZoneFor(position);
  return ROOM_POSITION_ZONES.includes(zone) ? zone : null;
};

/** "to the south-west of the main room", said mid-sentence. */
export const roomPositionPhrase = (position) => {
  const zone = roomPositionZone(position);
  if (!zone) return 'not placed relative to the main room';
  return `to the ${ROOM_POSITION_LABELS[zone].toLowerCase()} of the main room`;
};

/** Main room first, then by name — the same order the API returns. */
export const sortRooms = (rooms) => [...(rooms || [])].sort(
  (a, b) => (Number(!!b.is_main) - Number(!!a.is_main))
    || String(a.name || '').localeCompare(String(b.name || '')),
);

/**
 * How a FREESTANDING tank's placement reads in one short phrase.
 *
 * Cowork beta run, NIT 7: a freestanding row said "unplaced" even when
 * the operator had given it a Location — the one thing the row exists
 * to show. A keyword becomes "on the north wall"; free text that names
 * no wall is still shown verbatim, because the operator wrote it and it
 * means something; only a genuinely empty Location reads as unplaced.
 */
export const freestandingPlaceLabel = (tank) => {
  const text = String(tank?.location || '').trim();
  if (!text) return 'unplaced';
  const zone = wallZoneFor(text);
  if (zone === 'elsewhere') return text;
  // `zonePhrase` is written for a sentence ABOUT the map; this label is
  // about the ROOM, so the map is trimmed off the end. Two shapes to
  // strip, not one: a wall reads "... of the room map" but the middle
  // reads "... on the map", and a bare `.replace(' map', '')` left that
  // one as "in the middle of the room on the".
  return zonePhrase(zone).replace(/ on the map$| map$/, '');
};

/**
 * The first line of a free-text field, capped, for a tooltip or a chip.
 *
 * `Room.notes` and friends are TextFields: unbounded and multi-line.
 * Concatenating one raw into a `title` produced a tooltip as long as
 * whatever the operator once typed.
 */
export const firstLine = (text, max = 120) => {
  const line = String(text || '').split(/\r?\n/)[0].trim();
  if (!line) return '';
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
};

/**
 * The order a PHONE lists wall groups in (operator, 2026-09-04: "a
 * phone is a list, not a map").
 *
 * The brief names six — North, East, South, West, Middle, Elsewhere —
 * and with only those populated that is exactly what renders. The
 * corners are interleaved where they physically belong so a rack in one
 * is never silently dropped or, worse, filed under a wall it is not on.
 */
export const PHONE_WALL_ORDER = [
  'north', 'northeast', 'east', 'southeast',
  'south', 'southwest', 'west', 'northwest',
  'center', 'elsewhere',
];

/**
 * The room a rack, freestanding tank or THING belongs to, as a string id.
 *
 * ⚠️ `effective_room_id` FIRST (review of S3-06.4). A `Thing` placed on
 * a rack or in a tank carries `room: null` — it is in the RACK's room —
 * so bucketing on the column drew a fogger on a bug-room rack under the
 * MAIN room while the same row's `where` sentence said "Bug Room". The
 * server answers the walk once (`finding.effective_room`) and this reads
 * it; rows that have no such field (racks, tanks) fall through to `room`
 * exactly as before.
 */
export const roomIdOf = (thing) => {
  const value = thing?.effective_room_id ?? thing?.room;
  if (value === null || value === undefined || value === '') return null;
  return typeof value === 'object' ? String(value.id) : String(value);
};

/**
 * A SHELF AS A PICK in `EnclosurePicker` (`includeShelves`), Cowork QA
 * 2026-10-28: the clutch panel's Where picker offered tanks only, so an egg
 * cup could not be put straight on a shelf from the panel. A shelf option's
 * value is `shelf:<id>` so it can never be read as a tank id; every other
 * value is a tank id exactly as before. The placement itself goes through
 * the ONE cup door the racks page uses (`placeAnimalGroup`).
 */
export const SHELF_PICK_PREFIX = 'shelf:';

export const shelfPickValue = (shelfId) => `${SHELF_PICK_PREFIX}${shelfId}`;

/** The shelf id of a `shelf:<id>` pick, or null for a tank / empty pick. */
export const shelfIdOfPick = (value) => {
  const text = String(value || '');
  return text.startsWith(SHELF_PICK_PREFIX)
    ? (text.slice(SHELF_PICK_PREFIX.length) || null) : null;
};
